import React, { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { BillingProvider } from './contexts/BillingContext'
import { initAnalytics } from './services/analytics'
import Layout from './components/Layout'
import UpgradePrompt from './components/UpgradePrompt'
import AuthPage from './pages/AuthPage'
import DashboardPage from './pages/DashboardPage'
import KanbanPage from './pages/KanbanPage'
import AnalysisPage from './pages/AnalysisPage'
import AssetsPage from './pages/AssetsPage'
import ScriptsPage from './pages/ScriptsPage'
import CalendarPage from './pages/CalendarPage'
import OnboardingPage from './pages/OnboardingPage'
import ProfilePage from './pages/ProfilePage'
import LandingPage from './pages/LandingPage'
import YouTubePage from './pages/YouTubePage'
import LinkedInPage from './pages/LinkedInPage'
import PricingPage from './pages/PricingPage'
import CheckoutSuccessPage from './pages/CheckoutSuccessPage'
import CheckoutCancelPage from './pages/CheckoutCancelPage'
import RefundPolicyPage from './pages/RefundPolicyPage'
import { Toaster } from './components/ui/sonner'
import './App.css'

// Apply dark mode globally
document.documentElement.classList.add('dark')

function PrivateRoute({ children, fallback }) {
  const { user, loading } = useAuth()
  if (loading) {
    return (
      <div className="h-screen bg-ds-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-ds-red border-t-transparent rounded-full animate-spin" />
          <p className="type-label text-ds-muted">Cargando RDK·CC...</p>
        </div>
      </div>
    )
  }
  if (!user) return fallback ?? <Navigate to="/auth" replace />
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />
  return children
}

function AppRoutes() {
  const { user } = useAuth()
  return (
    <Routes>
      <Route path="/auth" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route
        path="/onboarding"
        element={
          !user ? (
            <Navigate to="/auth" replace />
          ) : user.onboarding_completed ? (
            <Navigate to="/" replace />
          ) : (
            <OnboardingPage />
          )
        }
      />
      <Route
        path="/*"
        element={
          <PrivateRoute fallback={<LandingPage />}>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/kanban" element={<KanbanPage />} />
                <Route path="/analysis" element={<AnalysisPage />} />
                <Route path="/assets" element={<AssetsPage />} />
                <Route path="/scripts" element={<ScriptsPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/youtube" element={<YouTubePage />} />
                <Route path="/linkedin" element={<LinkedInPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  )
}

function App() {
  useEffect(() => {
    initAnalytics()
  }, [])

  return (
    <AuthProvider>
      <BrowserRouter>
        <BillingProvider>
          <Toaster theme="dark" position="bottom-right" richColors />
          <AppRoutes />
          <UpgradePrompt />
        </BillingProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}

export default App
